"use client";

import Link from "next/link";
import { ArrowUpRight, DollarSign, Clock, User, Layers, ChevronRight } from "lucide-react";

import { getImageUrl } from "@/app/lib/image-util";

const resolveContentImages = (html: string) => {
  if (!html) return "";
  const cdn = (process.env.NEXT_PUBLIC_IMAGE_CDN || "https://pub-baa0491908f943acb30826fc7abe1c76.r2.dev").replace(/\/+$/, "");
  return html.replace(/<img([^>]+)src=["'](?:\/?)(project\/[^"']+)["']/gi, `<img$1src="${cdn}/$2"`);
};

/* ================= TYPES ================= */
export interface Project {
  _id: string;
  title: string;
  subtitle: string;
  slug: string;
  content: string;
  coverImage: string | null;
  technologies: string[];
  client: string;
  cost: string;
  duration: string;
  liveUrl: string;
  publishedAt: string;
}

interface ProjectClientProps {
  project: Project;
}

/* ================= COMPONENT ================= */
export default function ProjectClient({ project }: ProjectClientProps) {

  return (
    <main className="max-w-7xl mx-auto px-4 py-10 md:py-14 animate-in fade-in duration-500">
      
      {/* BREADCRUMB */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8 overflow-x-auto whitespace-nowrap pb-2">
        <Link href="/" className="hover:text-black transition-colors">Home</Link>
        <ChevronRight size={14} />
        <Link href="/projects" className="hover:text-black transition-colors">Projects</Link>
        <ChevronRight size={14} />
        <span className="text-gray-900 font-medium truncate max-w-[200px] md:max-w-xs">{project.title}</span>
      </nav>

      {/* HEADER */}
      <header className="mb-12 max-w-4xl">
        <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">{project.title}</h1>
        {project.subtitle && <p className="text-xl md:text-2xl text-gray-500 leading-relaxed">{project.subtitle}</p>}
      </header>

      {/* COVER IMAGE */}
      {project.coverImage && (
        <div className="mb-16 rounded-3xl overflow-hidden shadow-sm aspect-video bg-gray-100 border border-gray-100">
          <img src={getImageUrl(project.coverImage)} alt={project.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        
        {/* SIDEBAR */}
        <aside className="lg:col-span-4 lg:order-2 space-y-8">
          <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 sticky top-24">
            <h3 className="font-bold text-gray-900 mb-6 uppercase tracking-wider text-xs">Project Info</h3>
            <div className="space-y-6">
              {project.client && (
                <div>
                  <span className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase mb-1"><User size={12}/> Client</span>
                  <p className="font-medium text-gray-900">{project.client}</p>
                </div>
              )}
              {project.cost && (
                <div>
                  <span className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase mb-1"><DollarSign size={12}/> Budget</span>
                  <p className="font-medium text-gray-900">{project.cost}</p>
                </div>
              )}
              {project.duration && (
                <div>
                  <span className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase mb-1"><Clock size={12}/> Timeline</span>
                  <p className="font-medium text-gray-900">{project.duration}</p>
                </div>
              )}
              {project.technologies && project.technologies.length > 0 && (
                <div>
                  <span className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase mb-1"><Layers size={12}/> Technologies</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {project.technologies.map(t => (
                      <span key={t} className="bg-white border border-gray-200 px-3 py-1 rounded-full text-xs font-medium text-gray-700">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {project.liveUrl && (
                <div className="pt-6 border-t border-gray-200 mt-6">
                  <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-black text-white py-3.5 rounded-xl font-medium hover:bg-gray-800 transition-all shadow-lg shadow-gray-200">
                    Visit Live Site <ArrowUpRight size={18} />
                  </a>
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* ARTICLE */}
        <div className="lg:col-span-8 lg:order-1">
          <article className="prose prose-lg prose-gray max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-img:rounded-2xl prose-a:text-blue-600">
            <div dangerouslySetInnerHTML={{ __html: resolveContentImages(project.content) }} />
          </article>
          <div className="mt-20 pt-10 border-t border-gray-100 flex justify-between items-center">
            <Link href="/projects" className="text-gray-500 font-medium hover:text-black transition-colors">← Back to All Projects</Link>
          </div>
        </div>

      </div>
    </main>
  );
}